import { useState } from 'react';
import { infoToolTip } from '../../assets/hostedassets';
import LoadingSpinner from '../../common/_custom/LoadingSpinner/LoadingSpinner';
import styles from './RiskIndicators.module.scss';
import RiskIndicatorTable from './RiskIndicatorTable/RiskIndicatorTable';
import Tooltip from '../../common/_custom/Tooltip/Tooltip';
import { RISK_HEADER_KEYS, RISK_MESSAGES_MAP } from './RiskIndicatorConstants';
import clsx from 'clsx';
import { useRiskIndicatorStates } from './useRiskIndicatorStates';
import A4Page from '../AicaPDFReport/A4Page/A4Page';
import { REPORT_SECTION_NAMES } from '../../store/pdfReport/pdfReport_constants';
import RiskIndicatorCard from './RiskIndicatorCard';
import HeaderBodyBox from '../../common/HeaderBodyBox/HeaderBodyBox';
import AskAicaQuestionBadge from '../../common/AskAicaQuestionBadge/AskAicaQuestionBadge';
import RegulatoryRiskDrawer from './RegulatoryRiskDrawer/RegulatoryRiskDrawer';

interface RiskProfile {
    [key: string]: number;
}

/*
    RECUR-27829: one cell of the per-checkType state map. `state` is the BE's
    RegulatoryRiskState. Optional throughout, because polus omits the whole map until
    underwriting-engine is deployed and the cell then falls back to riskProfile.
*/
export interface RegulatoryRiskCell {
    state: string;
    confirmedCount: number;
    probableCount: number;
    // Confirmed-and-open count for THIS row's own entity — lets a Confirmed pill's click
    // handler pick Open vs Disposed without re-deriving the row's group key.
    confirmedOpenCount?: number;
}

export interface Row {
    docId?: string;
    name: string;
    type: string;
    riskProfile: RiskProfile;
    riskState?: Record<string, RegulatoryRiskCell>;
    designation?: string;
    identifier?: string;
    shareholdingPercentage?: number | null;
}

export interface RegulatoryRiskDrawerState {
    row: Row;
    checkType: string;
}

export interface RegulatoryRiskHitsResult {
    matchCount: number;
    matches: Record<string, any>[];
    // RECUR-27829. `matches` stays as the union of both sections so nothing that read it
    // before breaks; the drawer renders from these two.
    confirmedMatches?: Record<string, any>[];
    probableMatches?: Record<string, any>[];
}

export interface ViewerState {
    url: string;
    mimeType: string;
    heading: string;
    docId: string;
}

const RiskIndicators = ({
    isPDFReport = false,
    onOpenLegalCases,
}: {
    isPDFReport?: boolean;
    onOpenLegalCases?: (row: Row, state: RegulatoryRiskCell) => void;
}) => {
    const [rows, setRows] = useState<Row[]>([]);

    const {
        loading,
        getStatusMessage,
        RiskDotMessage,
        regulatoryRiskDrawerState,
        regulatoryRiskHits,
        regulatoryRiskHitsLoading,
        onOpenRegulatoryRisk,
        closeRegulatoryRiskDrawer,
        regulatoryRiskDrawerConfig,
    } = useRiskIndicatorStates({
        rows,
        setRows,
        autoFetch: true,
    });

    // Routes the pill's open callback by checkType: by design, Legal Cases scrolls to the
    // matching table section instead of opening the regulatory-risk drawer.
    const handleOpenRegulatoryRisk = (row: Row, checkType: string) => {
        if (checkType === RISK_HEADER_KEYS.LEGAL_CASES_KEY) {
            const cell = row.riskState?.[checkType];
            if (!cell) return;
            onOpenLegalCases?.(row, cell);
            return;
        }
        onOpenRegulatoryRisk(row, checkType);
    };

    const headerRight = (
        <div className={styles.DataMessage}>
            <span className={styles.Loader}>{loading && <LoadingSpinner color="#6021B3" height="1.125rem" />}</span>
            <span className={styles.Message}>{getStatusMessage()}</span>
        </div>
    );

    const titleWithTooltip = (
        <div className={styles.HeadingMessage}>
            <span>Regulatory Checks</span>
            <span className={styles.InfoIcon}>
                {!isPDFReport && (
                    <Tooltip
                        content={RISK_MESSAGES_MAP[RISK_HEADER_KEYS.RISK_INDICATOR_KEY] || ''}
                        direction="right"
                        style={{ top: '100%' }}
                    >
                        <img src={infoToolTip} height={'14px'} />
                    </Tooltip>
                )}
            </span>
        </div>
    );

    const bodyContent = rows?.length > 0 && !loading && (
        <>
            {isPDFReport ? (
                <div className={styles.CardsContainer}>
                    {rows.map((row, index) => (
                        <RiskIndicatorCard key={index} row={row} />
                    ))}
                </div>
            ) : (
                <div className={styles.TableWrapper}>
                    <div className={styles.TableContainer}>
                        <RiskIndicatorTable
                            rows={rows}
                            setRows={setRows}
                            isPDFReport={isPDFReport}
                            onOpenRegulatoryRisk={handleOpenRegulatoryRisk}
                        />
                    </div>
                </div>
            )}
            <div className={styles.FooterContainer}>
                <div className={styles.FooterMessage}>
                    <span className={styles.Note}>Note:</span> Risks are flagged based on the legal name match (please verify).
                </div>
                {/* <div className={styles.RiskDotMessage}>{RiskDotMessage()}</div> */}
            </div>
        </>
    );

    const render = (
        <div className={clsx(styles.ContainerWrapper, { [styles.PDFReport]: isPDFReport })}>
            <HeaderBodyBox
                title={!isPDFReport ? titleWithTooltip : 'Regulatory Checks'}
                headerRight={headerRight}
                className={clsx({ [styles.DataAvailable]: rows?.length > 0 && !loading })}
                isPDFReport={isPDFReport}
            >
                {bodyContent}
            </HeaderBodyBox>
        </div>
    );

    if (isPDFReport) return rows?.length ? <A4Page sectionName={REPORT_SECTION_NAMES.REGULATORY_INDICATORS}>{render}</A4Page> : <></>;

    return (
        <>
            {render}
            {!!regulatoryRiskDrawerState && (
                <RegulatoryRiskDrawer
                    checkType={regulatoryRiskDrawerState.checkType}
                    entityName={regulatoryRiskDrawerState.row?.name}
                    matchCount={regulatoryRiskHits?.matchCount ?? 0}
                    matches={regulatoryRiskHits?.matches ?? []}
                    confirmedMatches={regulatoryRiskHits?.confirmedMatches}
                    probableMatches={regulatoryRiskHits?.probableMatches}
                    config={regulatoryRiskDrawerConfig?.[regulatoryRiskDrawerState.checkType]}
                    isLoading={regulatoryRiskHitsLoading}
                    onClose={closeRegulatoryRiskDrawer}
                />
            )}
        </>
    );
};

export default RiskIndicators;
